// exportUtils.js - CSV export functionality

export const formatDateForFilename = (date = new Date()) => {
  return date.toISOString().split('T')[0];
};

export const generateCSV = (data, columns) => {
  if (!data || data.length === 0) return '';

  // Header row
  const headers = columns.map(col => col.label || col.key).join(',');

  // Data rows
  const rows = data.map(row => {
    return columns.map(col => {
      let value = row[col.key];

      // Apply formatter if provided
      if (col.formatter) {
        value = col.formatter(value, row);
      }

      // Handle null/undefined
      if (value === null || value === undefined) {
        return '';
      }

      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    }).join(',');
  });

  return [headers, ...rows].join('\n');
};

export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportAnalyticsData = ({
  overview,
  bySource,
  byDate,
  topItems,
  period,
  startDate,
  endDate
}) => {
  const dateRange = period === 'custom'
    ? `${startDate}_to_${endDate}`
    : period;

  // Export Overview
  if (overview) {
    const overviewColumns = [
      { key: 'metric', label: 'Metric' },
      { key: 'value', label: 'Value' }
    ];
    const overviewData = [
      { metric: 'Total Orders', value: overview.total_orders },
      { metric: 'Total Revenue', value: `$${overview.total_revenue}` },
      { metric: 'Average Order Value', value: `$${overview.avg_order_value}` },
      { metric: 'Unique Customers', value: overview.unique_customers },
      { metric: 'Orders with Phone', value: overview.orders_with_phone },
      { metric: 'Orders with Email', value: overview.orders_with_email }
    ];
    const overviewCSV = generateCSV(overviewData, overviewColumns);
    downloadCSV(overviewCSV, `analytics_overview_${dateRange}.csv`);
  }

  // Export by Source
  if (bySource && bySource.length > 0) {
    const sourceColumns = [
      { key: 'source', label: 'Source' },
      { key: 'order_count', label: 'Orders' },
      { key: 'revenue', label: 'Revenue' },
      { key: 'avg_order', label: 'Avg Order' },
      { key: 'unique_customers', label: 'Unique Customers' }
    ];
    const sourceCSV = generateCSV(bySource, sourceColumns);
    downloadCSV(sourceCSV, `analytics_by_source_${dateRange}.csv`);
  }

  // Export by Date
  if (byDate && byDate.length > 0) {
    const dateColumns = [
      { key: 'order_date', label: 'Date' },
      { key: 'order_count', label: 'Orders' },
      { key: 'revenue', label: 'Revenue' },
      { key: 'unique_customers', label: 'Unique Customers' }
    ];
    const dateCSV = generateCSV(byDate, dateColumns);
    downloadCSV(dateCSV, `analytics_by_date_${dateRange}.csv`);
  }

  // Export Top Items
  if (topItems && topItems.length > 0) {
    const itemColumns = [
      { key: 'item_name', label: 'Item' },
      { key: 'total_quantity', label: 'Quantity' },
      { key: 'order_count', label: 'Order Count' }
    ];
    const itemCSV = generateCSV(topItems, itemColumns);
    downloadCSV(itemCSV, `analytics_top_items_${dateRange}.csv`);
  }
};
